import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { ContractDetails } from '../models/contract-request/contract-details.model';
import { ContractRequestStatus } from '../models/contract-request/contract-request-status.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LocumsMarginCalculatorSlice } from '../store/locums-margin-calculator.slice';
import { MarginAdditionalRate } from '../models/contract-request/margin-additional-rate.model';
import { MarginExpense } from '../models/margin-expense.model';
import { MarginPipelineRequest } from '../models/contract-request/margin-pipeline-request.model';
import { Observable } from 'rxjs';
import { PayRates } from '../models/contract-request/pay-rates.model';
import { PipelineRequestStatusEnum } from '../enums/pipeline-request-status.enum';
import { ProviderDetails } from '../models/contract-request/provider-details.model';
import { combine } from 'src/app/shared/utilities/route';
import { mapSliceToSaveRequest } from '../helpers/locums-margin-calculator.helper';
import { MarginRtoDate } from '../models/margin-rto-date.model';

@Injectable({ providedIn: 'root' })
export class LocumsMarginContractRequestService {
    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova/locums/margin-pipeline-requests/');
    }

    getPipelineRequestStatusByMarginId(marginId: number): Observable<ContractRequestStatus> {
        const url = combine(this.coreApiPath, `margins/${marginId}/statuses`);
        return this.http.get<ContractRequestStatus>(url);
    }

    getPipelineRequestStatusByPipelineRequestId(pipelineRequestId: number): Observable<ContractRequestStatus> {
        const url = combine(this.coreApiPath, `${pipelineRequestId}/statuses`);
        return this.http.get<ContractRequestStatus>(url);
    }

    getProviderDetails(pipelineRequestId: number): Observable<ProviderDetails> {
        const url = combine(this.coreApiPath, `${pipelineRequestId}/provider-details`);
        return this.http.get<ProviderDetails>(url);
    }

    getPipelineRequests(marginId: number): Observable<MarginPipelineRequest[]> {
        const url = combine(this.coreApiPath, `margins/${marginId}/pipeline-requests`);
        return this.http.get<MarginPipelineRequest[]>(url);
    }

    getContractDetails(pipelineRequestId: number): Observable<ContractDetails> {
        const url = combine(this.coreApiPath, `${pipelineRequestId}/contract-details`);
        return this.http.get<ContractDetails>(url);
    }

    getPayRates(pipelineRequestId: number): Observable<PayRates> {
        const url = combine(this.coreApiPath, `${pipelineRequestId}/pay-rates`);
        return this.http.get<PayRates>(url);
    }

    getAdditionalRates(pipelineRequestId: number): Observable<MarginAdditionalRate[]> {
        const url = combine(this.coreApiPath, `${pipelineRequestId}/additional-rates`);
        return this.http.get<MarginAdditionalRate[]>(url);
    }

    getExpenses(pipelineRequestId: number): Observable<MarginExpense[]> {
        const url = combine(this.coreApiPath, `${pipelineRequestId}/expenses`);
        return this.http.get<MarginExpense[]>(url);
    }

    getRtoDates(pipelineRequestId: number): Observable<MarginRtoDate[]> {
        const url = combine(this.coreApiPath, `${pipelineRequestId}/rto-dates`);
        return this.http.get<MarginRtoDate[]>(url);
    }

    getNotes(pipelineRequestId: number): Observable<string> {
        const url = combine(this.coreApiPath, `${pipelineRequestId}/notes`);
        return this.http.get(url, { responseType: 'text' });
    }

    createContractRequest(data: LocumsMarginCalculatorSlice): Observable<number> {
        const saveRequest = mapSliceToSaveRequest(data);

        const url = combine(this.coreApiPath, `pipelined/create`);
        return this.http.post<number>(url, saveRequest);
    }

    createPendingContractRequest(
        data: LocumsMarginCalculatorSlice,
        pipelineRequestStatus: PipelineRequestStatusEnum
    ): Observable<number> {
        const saveRequest = mapSliceToSaveRequest(data);
        const params = new HttpParams().set('PipelineRequestStatus', pipelineRequestStatus.toString());
        const url = combine(this.coreApiPath, `pending/create`);
        return this.http.post<number>(url, saveRequest, { params });
    }

    updatePendingToPipelinedRequest(pipelineRequestId: number): Observable<number> {
        const url = combine(this.coreApiPath, `pipelined/${pipelineRequestId}/approve`);
        return this.http.post<number>(url, null);
    }

    updatePendingAmToPendingContractRequest(pipelineRequestId: number): Observable<number> {
        const url = combine(this.coreApiPath, `pending/${pipelineRequestId}/approve`);
        return this.http.post<number>(url, null);
    }

    cancelContractRequest(pipelineRequestId: number): Observable<void> {
        const url = combine(this.coreApiPath, `pending/${pipelineRequestId}/cancel`);
        return this.http.post<void>(url, null);
    }

    updatePendingAmContractRequest(data: LocumsMarginCalculatorSlice, pipelineRequestId: number): Observable<number> {
        const saveRequest = mapSliceToSaveRequest(data);
        const url = combine(this.coreApiPath, `pending/${pipelineRequestId}/update`);
        return this.http.post<number>(url, saveRequest);
    }
}
